import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';
import LandingPage from './LandingPage';
import Auth from './Auth';
import OldAdminDashboard from './AdminDashboard';
import BetSlip from './BetSlip';
import SocialFeed from './SocialFeed';
import Marketplace from './Marketplace';
import MarketplaceV2 from './MarketplaceV2';
import MyAccount from './MyAccount';
import DisclaimerModal from './DisclaimerModal';
import AnnouncementModal from './AnnouncementModal';
import BacktestingEngine from './BacktestingEngine';
import DailyPicks from './DailyPicks';
import MyBets from './MyBets';
import PublicEdges from './PublicEdges';
import XLanding from './XLanding';
import useAnalytics from './hooks/useAnalytics';

// V2 UI imports
import { UiProvider, useUi } from './ui/UiProvider';
import './ui/theme/v2.css';
import './ui/shared/shared.css';
import AiEdgesTodayPageV2 from './ui/v2/pages/AiEdgesTodayPageV2';
import './ui/v2/pages/AiEdgesTodayPageV2.css';
import AskAiPageV2 from './ui/v2/pages/AskAiPageV2';
import './ui/v2/pages/AskAiPageV2.css';
import VerifiedPicksPageV2 from './ui/v2/pages/VerifiedPicksPageV2';
import './ui/v2/pages/VerifiedPicksPageV2.css';
import MyAccountPageV2 from './ui/v2/pages/MyAccountPageV2';
import './ui/v2/pages/MyAccountPageV2.css';
import AdminDashboard from './ui/v2/pages/AdminDashboard';
import './ui/v2/pages/AdminDashboard.css';
import { trackLogin, trackPageView, trackBetAdded, trackChatMessage } from './utils/tracking';
import './ui/v2/components/PickCardV2.css';
import './ui/v2/components/SellerCardV2.css';

const API_ENDPOINT = 'https://bvjnueuoz7cb3kwtbabltt6icy0tutqe.lambda-url.us-east-1.on.aws';
const ACCOUNT_API = 'https://563t3yrmw5d42zyd7gw6eeeun40htlid.lambda-url.us-east-1.on.aws';

function App() {
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [view, setView] = useState('chat');
  const [betSlipItems, setBetSlipItems] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const analytics = useAnalytics();

  const addToBetSlip = async (pick) => {
    // Add to local state for immediate UI feedback
    setBetSlipItems(prev => [...prev, pick]);
    trackBetAdded(pick.matchup);
    
    // Also save to API
    try {
      const token = localStorage.getItem('idToken');
      await fetch(`${ACCOUNT_API}/api/account/bets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          matchup: pick.matchup,
          selection: pick.selection,
          odds: pick.odds,
          game_time: pick.game_time || pick.gameTime,
          source: 'ai_pick'
        })
      });
      // Remove from local slip after successful save
      setBetSlipItems(prev => prev.filter(p => p.id !== pick.id));
    } catch (e) {
      console.error('Failed to save bet:', e);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('idToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser({ 
          email: payload.email, 
          name: payload.name,
          sub: payload.sub,
          role: payload['custom:role'] || 'user'
        });
        setShowAuth(false);
        
        // Show announcement if user hasn't seen it yet
        const hasSeenAnnouncement = localStorage.getItem('seen_30day_announcement');
        if (!hasSeenAnnouncement) {
          setShowAnnouncement(true);
        }
      } catch (e) {
        localStorage.removeItem('idToken');
      }
    }
  }, []);

  // Check subscription status when user logs in
  useEffect(() => {
    if (user?.sub) {
      checkSubscriptionStatus();
    }
  }, [user?.sub]);

  const checkSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const res = await fetch(`${ACCOUNT_API}/api/account/subscription`, {
        headers: { 'Authorization': token }
      });
      const data = await res.json();
      
      setSubscriptionStatus(data.status); // 'trial', 'active', 'past_due', 'cancelled'
      
      if (data.trial_days_remaining !== undefined) {
        setTrialDaysLeft(data.trial_days_remaining);
      }
      
      // Update user with admin status from database
      if (data.is_admin) {
        setUser(prev => prev ? { ...prev, role: 'admin' } : prev);
      }
      
      // Show expired modal if trial ended and no active subscription
      if (data.status === 'cancelled' || (data.status === 'trial' && data.trial_days_remaining <= 0)) {
        setShowTrialExpiredModal(true);
      }
    } catch (e) {
      // Fail open - allow access if API fails
      setSubscriptionStatus('active');
    }
  };

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    setShowAuth(false);
    trackLogin();
    
    // Show announcement if user hasn't seen it yet
    const hasSeenAnnouncement = localStorage.getItem('seen_30day_announcement');
    if (!hasSeenAnnouncement) {
      setShowAnnouncement(true);
    }
  };

  const handleAnnouncementClose = () => {
    setShowAnnouncement(false);
    localStorage.setItem('seen_30day_announcement', 'true');
  };

  const handleLogout = () => {
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setMessages([]);
  };

  const sendMessage = async () => {
    if (!query.trim()) return;

    const userMessage = { role: 'user', content: query };
    setMessages(prev => [...prev, userMessage]);
    setQuery('');
    setLoading(true);

    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.post(API_ENDPOINT, {
        prompt: query
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const aiMessage = {
        role: 'assistant',
        content: response.data.message || response.data.response || 'No response',
        quickActions: response.data.quick_actions || []
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage = {
        role: 'error',
        content: `Error: ${error.message}`
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  // Handle special routes first
  const path = window.location.pathname;
  
  // /x landing page (from Twitter)
  if (path === "/x") {
    return <XLanding />;
  }
  
  // /edges public picks page
  if (path === "/edges") {
    return <PublicEdges />;
  }

  // Trial banner helper
  const getTrialBanner = () => {
    if (subscriptionStatus === 'active') return null;
    if (subscriptionStatus !== 'trial' || trialDaysLeft === null) return null;
    
    if (trialDaysLeft <= 2) {
      return (
        <div className="trial-banner trial-urgent">
          ⚠️ Trial ends in {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''}! 
          <button onClick={() => setView('account')}>Subscribe Now</button>
        </div>
      );
    } else if (trialDaysLeft <= 9) {
      return (
        <div className="trial-banner trial-warning">
          ⏰ {trialDaysLeft} days left in your trial
          <button onClick={() => setView('account')}>Subscribe</button>
        </div>
      );
    }
    return null;
  };

  // Show landing page if not logged in and not trying to auth
  if (!user && !showAuth) {
    return <LandingPage onGetStarted={() => setShowAuth(true)} />;
  }

  // Show auth if user clicked login/signup
  if (!user && showAuth) {
    return <Auth onAuthSuccess={handleAuthSuccess} onBack={() => setShowAuth(false)} />;
  }

  return (
    <div className="App">
      {/* Trial Expired Modal */}
      {showTrialExpiredModal && (
        <div className="modal-overlay">
          <div className="trial-expired-modal">
            <h2>⏰ Trial Ended</h2>
            <p>Your 30-day free trial has expired. Subscribe to continue using BetPilot AI.</p>
            <div className="trial-expired-features">
              <p>✓ Unlimited AI analysis</p>
              <p>✓ Daily verified picks</p>
              <p>✓ Performance tracking</p>
            </div>
            <button className="subscribe-btn" onClick={() => { setShowTrialExpiredModal(false); setView('account'); }}>
              Subscribe - $20/month
            </button>
            <button className="dismiss-btn" onClick={() => setShowTrialExpiredModal(false)}>
              Maybe Later
            </button>
          </div>
        </div>
      )}

      {/* Trial Banner */}
      {getTrialBanner()}

      <header className="header">
        <h1>🎯 BetPilot</h1>
        <div className="user-info">
          <button 
            onClick={() => { setView('chat'); trackPageView('chat'); }}
            className={`nav-btn ${view === 'chat' ? 'active' : ''}`}
          >
            💬 Ask the AI
          </button>
          <button 
            onClick={() => { setView('picks'); trackPageView('ai_edges'); }}
            className={`nav-btn ${view === 'picks' ? 'active' : ''}`}
          >
            🎯 AI Edges Today
          </button>
          <button 
            onClick={() => { setView('marketplace'); trackPageView('verified_picks'); }}
            className={`nav-btn ${view === 'marketplace' ? 'active' : ''}`}
          >
            ✅ Verified Picks
          </button>
          <button 
            onClick={() => { setView('account'); trackPageView('account'); }}
            className={`nav-btn ${view === 'account' ? 'active' : ''}`}
          >
            👤 My Account
          </button>
          {user.role === 'admin' && (
            <>
              <button 
                onClick={() => setView('backtest')}
                className={`nav-btn ${view === 'backtest' ? 'active' : ''}`}
              >
                📈 Backtest
              </button>
              <button 
                onClick={() => setView(view === 'admin' ? 'chat' : 'admin')}
                className="admin-toggle"
              >
                {view === 'admin' ? '💬 Chat' : '⚙️ Admin'}
              </button>
            </>
          )}
          <span>Welcome, {user.name || user.email}</span>
          <button onClick={handleLogout} className="logout-btn">Logout</button>
        </div>
      </header>

      {view === 'admin' && user.role === 'admin' ? (
        <AdminDashboard user={user} />
      ) : view === 'picks' ? (
        <ViewPicks addToBetSlip={addToBetSlip} user={user} analytics={analytics} />
      ) : view === 'marketplace' ? (
        <ViewMarketplace user={user} />
      ) : view === 'account' ? (
        <ViewAccount user={user} onShowDisclaimer={() => setShowDisclaimer(true)} />
      ) : view === 'bets' ? (
        <MyBets user={user} betSlipItems={betSlipItems} setBetSlipItems={setBetSlipItems} />
      ) : view === 'backtest' ? (
        <BacktestingEngine user={user} />
      ) : (
        <ViewChat 
          user={user} 
          messages={messages} 
          setMessages={setMessages}
          query={query}
          setQuery={setQuery}
          loading={loading}
          setLoading={setLoading}
          sendMessage={sendMessage}
        />
      )}
      
      {showDisclaimer && (
        <DisclaimerModal 
          user={user} 
          onAccept={() => setShowDisclaimer(false)} 
        />
      )}
      
      {showAnnouncement && (
        <AnnouncementModal onClose={handleAnnouncementClose} />
      )}
    </div>
  );
}

// View wrapper components for v1/v2 switching
const ViewPicks = ({ addToBetSlip, user, analytics }) => {
  const { isV2 } = useUi();
  return isV2 ? (
    <AiEdgesTodayPageV2 onAddToBetSlip={addToBetSlip} user={user} />
  ) : (
    <DailyPicks onAddToBetSlip={addToBetSlip} user={user} analytics={analytics} />
  );
};

const ViewMarketplace = ({ user }) => {
  const { isV2 } = useUi();
  return isV2 ? <VerifiedPicksPageV2 user={user} /> : <MarketplaceV2 user={user} />;
};

const ViewAccount = ({ user, onShowDisclaimer }) => {
  const { isV2 } = useUi();
  // Always use V1 MyAccount for full functionality, V2 just adds color overrides via CSS
  return (
    <div className={isV2 ? 'v2-account-wrapper' : ''}>
      <MyAccount user={user} onShowDisclaimer={onShowDisclaimer} />
    </div>
  );
};

const ViewChat = ({ user, messages, setMessages, query, setQuery, loading, setLoading, sendMessage }) => {
  const { isV2 } = useUi();
  
  if (isV2) {
    return <AskAiPageV2 user={user} />;
  }
  
  return (
    <div className="main-content">
      <div className="chat-section">
        <div className="chat-container">
          <div className="messages">
            {messages.map((msg, idx) => (
              <div key={idx} className={`message ${msg.role}`}>
                <div className="message-content">{msg.content}</div>
                {msg.quickActions && msg.quickActions.length > 0 && (
                  <div className="quick-actions">
                    {msg.quickActions.map((action, i) => (
                      <button key={i} onClick={() => setQuery(action.prompt)} className="quick-action-btn">
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {loading && <div className="message assistant loading">Analyzing...</div>}
          </div>
          <div className="input-container">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Ask about betting opportunities..."
              disabled={loading}
            />
            <button onClick={sendMessage} disabled={loading || !query.trim()}>Send</button>
          </div>
        </div>
        {messages.length === 0 && (
          <div className="examples">
            <h3>Try asking:</h3>
            <button onClick={() => setQuery("What's the best bet for tonight?")}>Best bet tonight?</button>
            <button onClick={() => setQuery("What games are on today?")}>Today's games</button>
            <button onClick={() => setQuery("Explain point spreads")}>What is a spread?</button>
          </div>
        )}
      </div>
      <BetSlip user={user} />
    </div>
  );
};

// Wrap App with UiProvider
const AppWithUi = () => (
  <UiProvider>
    <App />
  </UiProvider>
);

export default AppWithUi;
